<!-- A cook's book — one person's book: their name and kitchen, then every
     recipe they've handed down. Reached only from the shelf. Links onward to
     a recipe's sheet (which opens in editing mode when it is still blank),
     starting a new recipe, and the printable booklet. -->
<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  IonPage, IonHeader, IonToolbar, IonButtons, IonBackButton, IonContent,
} from '@ionic/vue';
import HdDialog from '../components/HdDialog.vue';
import HdSheet from '../components/HdSheet.vue';
import HdIcon from '../components/HdIcon.vue';
import { useRoute, useRouter } from 'vue-router';
import { isDurableMedia } from '../lib/audio-store.js';
import { deleteRecipeDeep } from '../lib/recipe-actions.js';
import {
  findCook, cooksReady,
  recipes, recipesReady, addRecipe, saveRecipe,
  marginNotes, marginNotesReady,
  steps, stepsReady,
  ingredients, ingredientsReady,
} from '../store.js';

const route = useRoute();
const router = useRouter();

// `route.params.cookId`, never `route.params.id` — this route is
// `/cook/:cookId` and never sets `id`. The generated version read `id`,
// which is always undefined, so `findCook(undefined)` returned null and this
// screen showed "Not found" for every cook that actually exists.
const cookId = computed(() => route.params.cookId);

const RELATIONSHIP_LABELS = {
  mum: 'Mum', dad: 'Dad', nonna: 'Nonna', nonno: 'Nonno',
  grandma: 'Grandma', grandpa: 'Grandpa', aunt: 'Aunt', uncle: 'Uncle', other: 'Other',
};
const relationshipLabel = (v) => RELATIONSHIP_LABELS[v] ?? v;

const loaded = ref(false);
onMounted(async () => {
  await Promise.all([cooksReady, recipesReady, marginNotesReady, stepsReady, ingredientsReady]);
  loaded.value = true;
});

const same = (a, b) => String(a) === String(b);

const cook = computed(() => findCook(cookId.value));
const recipesForCook = computed(() =>
  recipes.value.filter((r) => String(r.cookId) === String(cookId.value)));

const noteCount = (recipeId) =>
  marginNotes.value.filter((n) => String(n.recipeId) === String(recipeId)).length;

function formatDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// BY NAME, and with the param the route actually declares (`recipeId`, never
// `id`). ONE destination for every recipe: the sheet opens in editing mode by
// itself when there is nothing written yet. Sending still-capturing recipes to
// a separate Capture screen is what put people on a page in a different
// visual language with its own Record bar — after that screen had supposedly
// been retired.
function openRecipe(recipe) {
  router.push({ name: 'recipe-sheet', params: { recipeId: recipe.id } });
}
const openBooklet = () => router.push({ name: 'booklet', params: { cookId: cookId.value } });
const backToShelf = () => router.push({ name: 'shelf' });

const adding = ref(false);
const title = ref('');
function startAdding() {
  title.value = '';
  adding.value = true;
}
function cancelAdding() {
  adding.value = false;
}
function submitAdding() {
  const trimmed = title.value.trim();
  if (!trimmed) return;
  // Step two of the app's primary flow: name the dish, start recording. One
  // tap from here to the microphone, with the recipe already filed under
  // this cook and marked as still being captured.
  const row = addRecipe({ title: trimmed, cookId: cookId.value, status: 'capturing' });
  adding.value = false;
  openRecipe(row);
}

// ------------------------------------------------------------------ manage
//
// A visible ··· mark on every row, the same mark the recipe sheet's toolbar
// and the notes wear. It used to be press-and-hold with a sentence under the
// list explaining the gesture — a control that needs a caption is a control
// nobody finds, and the rest of the app had already stopped hiding its menus.

const manageId = ref(null);
const manageRecipe = computed(
  () => (manageId.value == null ? null : recipes.value.find((r) => same(r.id, manageId.value)) ?? null),
);
function openManage(recipe) { manageId.value = recipe.id; }

const stepsOf = (recipe) => steps.value.filter((s) => same(s.recipeId, recipe.id));
const ingsOf = (recipe) => ingredients.value.filter((i) => same(i.recipeId, recipe.id));
const notesOf = (recipe) => marginNotes.value.filter((n) => same(n.recipeId, recipe.id));

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const listOut = (parts) =>
  parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/* Never "Are you sure?". The sentence has to name the thing and count what
   goes with it, because the only person who can judge whether this is a
   mistake is the one reading it, and they can only judge it if they are told. */
const confirmDelete = ref(null);
function askDelete(recipe) {
  const bits = [];
  const s = stepsOf(recipe).length;
  const i = ingsOf(recipe).length;
  const notes = notesOf(recipe);
  const voices = notes.filter((n) => isDurableMedia(n.mediaUrl)).length;
  if (s) bits.push(plural(s, 'step'));
  if (i) bits.push(plural(i, 'ingredient'));
  if (notes.length) bits.push(`${plural(notes.length, 'note')} in the margin`);

  const who = cook.value?.name || 'them';
  const said = voices
    ? ` That includes ${plural(voices, 'recording')} of ${who} talking, which ${voices === 1 ? 'exists' : 'exist'} nowhere else.`
    : '';

  /* "Its 1 step go with it" is the sound of a sentence assembled by a machine,
     and this is the last thing anyone reads before losing something. */
  const single = bits.length === 1 && [s, i, notes.length].filter(Boolean)[0] === 1;

  confirmDelete.value = {
    id: recipe.id,
    header: `Delete ${recipe.title || 'this recipe'}?`,
    message: bits.length
      ? `Its ${listOut(bits)} ${single ? 'goes' : 'go'} with it.${said} Nothing brings any of it back.`
      : 'Nothing was ever written into it, so there is nothing else to lose.',
  };
}

/* One cascade, shared with the sheet's own Delete: lib/recipe-actions.js
   removes the audio before the rows, so nothing is left orphaned in IndexedDB
   counting against the quota that decides whether the NEXT recording saves. */
const doDelete = (id) => deleteRecipeDeep(id);

const renaming = ref(null);
const renameInputs = computed(() => [
  { name: 'title', type: 'text', value: renaming.value?.title ?? '', placeholder: 'Sugo di pomodoro' },
]);
const renameButtons = [
  { text: 'Cancel', role: 'cancel' },
  {
    text: 'Save',
    handler: (data) => {
      const title = String(data?.title ?? '').trim();
      const row = renaming.value ? recipes.value.find((r) => same(r.id, renaming.value.id)) : null;
      if (!title || !row) return;
      saveRecipe({ ...row, title });
    },
  },
];

const deleteButtons = computed(() => {
  const id = confirmDelete.value?.id;
  return [
    { text: 'Keep it', role: 'cancel' },
    { text: 'Delete', role: 'destructive', handler: () => doDelete(id) },
  ];
});

const manageButtons = computed(() => {
  const recipe = manageRecipe.value;
  if (!recipe) return [];
  return [
    {
      text: 'Fix the title',
      handler: () => {
        renaming.value = { id: recipe.id, title: recipe.title ?? '' };
        manageId.value = null;
      },
    },
    {
      text: 'Delete',
      role: 'destructive',
      handler: () => { askDelete(recipe); manageId.value = null; },
    },
    { text: 'Cancel', role: 'cancel' },
  ];
});
</script>

<template>
  <IonPage>
    <IonHeader class="hd-bar">
      <IonToolbar>
        <IonButtons slot="start">
          <IonBackButton default-href="/" text="Shelf" />
        </IonButtons>
      </IonToolbar>
    </IonHeader>

    <IonContent :fullscreen="true" class="hd-content">
      <div v-if="!loaded" class="hd-page waiting" aria-hidden="true"></div>

      <div v-else-if="!cook" class="hd-page missing">
        <h2 class="missing-h">Not found</h2>
        <p class="missing-p">That cook is no longer here. They may have been removed since this link was made.</p>
        <button type="button" class="hd-figure back" @click="backToShelf">Back to the shelf</button>
      </div>

      <div v-else class="hd-page book">
        <header class="head">
          <h1 class="title">{{ cook.name }}</h1>
          <p class="sub">
            {{ relationshipLabel(cook.relationship) }}<template v-if="cook.kitchen"> · {{ cook.kitchen }}</template>
          </p>
        </header>

        <p v-if="!recipesForCook.length" class="empty-copy">
          Nothing captured yet. The next time they cook, sit down and hit record.
        </p>

        <ul v-else class="recipes">
          <li v-for="recipe in recipesForCook" :key="recipe.id" class="recipe-li">
            <button
              type="button"
              class="row"
              @click="openRecipe(recipe)"
              @contextmenu.prevent="openManage(recipe)"
            >
              <span class="what">
                <span class="name">{{ recipe.title }}</span>
                <span v-if="recipe.status === 'capturing'" class="capturing hd-figure">Not written down yet</span>
                <span v-else class="meta">
                  {{ recipe.occasion || 'Kept' }}<span v-if="recipe.capturedAt" class="hd-figure date"> · {{ formatDate(recipe.capturedAt) }}</span>
                </span>
              </span>
              <span v-if="recipe.status !== 'capturing' && noteCount(recipe.id)" class="notes hd-figure">
                {{ noteCount(recipe.id) }} in the margin
              </span>
            </button>
            <button
              type="button"
              class="more"
              :aria-label="`What to do with ${recipe.title || 'this recipe'}`"
              @click="openManage(recipe)"
            >
              <HdIcon name="dots" :size="18" />
            </button>
          </li>
        </ul>

        <section class="actions">
          <section class="add">
            <button v-if="!adding" type="button" class="add-btn" @click="startAdding">
              <HdIcon name="plus" :size="15" :weight="2" />
              <span>Start a recipe</span>
            </button>

            <form v-else class="add-form" @submit.prevent="submitAdding">
              <label class="field">
                <span class="label">What are they making?</span>
                <input v-model="title" type="text" placeholder="Sugo di pomodoro" autocomplete="off" required />
              </label>
              <div class="row-actions">
                <button type="button" class="cancel" @click="cancelAdding">Cancel</button>
                <button type="submit" class="save">Start a recipe</button>
              </div>
            </form>
          </section>

          <button v-if="recipesForCook.length" type="button" class="booklet-link" @click="openBooklet">
            <HdIcon name="print" :size="15" />
            <span>Print the booklet</span>
          </button>
        </section>
      </div>

      <HdSheet
        :open="manageRecipe !== null"
        :header="manageRecipe?.title || 'This recipe'"
        :buttons="manageButtons"
        @dismiss="manageId = null"
      />

      <HdDialog
        :open="renaming !== null"
        header="Fix the title"
        :inputs="renameInputs"
        :buttons="renameButtons"
        @dismiss="renaming = null"
      />

      <!-- A dialog rather than an undo toast, because this one cascades: the
           steps, the amounts and the recordings go with it, and a recording
           deleted from IndexedDB is gone in a way nothing in this app can
           reverse. So the interruption happens BEFORE, and it counts out
           exactly what is about to be lost. -->
      <HdDialog
        :open="confirmDelete !== null"
        :header="confirmDelete?.header"
        :message="confirmDelete?.message"
        :buttons="deleteButtons"
        @dismiss="confirmDelete = null"
      />
    </IonContent>
  </IonPage>
</template>

<style scoped>
.waiting { min-height: 100%; }

.missing { padding: 48px 20px; min-height: 100%; text-align: center; color: var(--hd-pencil); }
.missing-h { margin: 0 0 8px; font-size: var(--hd-t-heading); font-weight: 500; color: var(--hd-ink); }
.missing-p { margin: 0 auto 20px; max-width: 26em; font-style: italic; }
.back { min-height: 44px; padding: 0 16px; background: none; border: 0; color: var(--hd-margin); cursor: pointer; }

.book { padding: 24px 20px 64px calc(var(--hd-margin-w) + 20px); min-height: 100%; position: relative; }
.book::before {
  content: '';
  position: absolute;
  top: 0; bottom: 0; left: var(--hd-margin-w);
  width: 1px;
  background: var(--hd-margin);
}

.head { margin-bottom: 18px; }
.title { margin: 0; font-size: var(--hd-t-title); font-weight: 500; line-height: 1.1; letter-spacing: -0.01em; }
.sub { margin: 4px 0 0; font-style: italic; color: var(--hd-pencil); }

.empty-copy { max-width: 30em; color: var(--hd-pencil); font-style: italic; line-height: var(--hd-rule); }

.recipes { margin: 0; padding: 0; list-style: none; }
.recipes li + li { border-top: 1px solid var(--hd-feint); }
.recipe-li { display: flex; align-items: stretch; }

/* Pencil-coloured and 44px square: a mark, not a button, until it is pressed. */
.more {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  min-height: 44px;
  margin-right: -12px;
  padding: 0;
  background: none;
  border: 0;
  color: var(--hd-pencil);
  cursor: pointer;
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex: 1 1 0;
  min-width: 0;
  min-height: 44px;
  padding: 14px 0;
  background: none;
  border: 0;
  text-align: left;
  cursor: pointer;
  color: var(--hd-ink);
  font-family: inherit;
}

.what { display: flex; flex-direction: column; gap: 2px; }
.name { font-size: var(--hd-t-name); }
.meta { font-family: var(--hd-sans); font-size: var(--hd-t-figure); color: var(--hd-pencil); }
.date { font-size: var(--hd-t-figure); }
.capturing { color: var(--hd-margin); font-size: var(--hd-t-figure); }
.notes { font-size: var(--hd-t-figure); white-space: nowrap; }

.actions { margin-top: 22px; display: flex; flex-direction: column; align-items: flex-start; gap: 14px; }

/* The same control the shelf's "Add a cook" is, so the two screens agree on
   what "add one" looks like. */
.add-btn {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: 7px;
  padding: 0 4px 0 0;
  background: none;
  border: 0;
  color: var(--hd-margin);
  font-family: var(--hd-sans);
  font-weight: 600;
  font-size: var(--hd-t-control);
  cursor: pointer;
}

.add-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 26em;
  padding: 16px;
  background: var(--hd-sheet);
}
.field { display: flex; flex-direction: column; gap: 4px; }
.field .label { font-family: var(--hd-sans); font-size: var(--hd-t-figure); color: var(--hd-pencil); }
.field input {
  min-height: 44px;
  padding: 8px 10px;
  border: 1px solid var(--hd-feint);
  background: var(--hd-paper);
  color: var(--hd-ink);
  font-family: var(--hd-serif);
  font-size: var(--hd-t-body);
}
.row-actions { display: flex; justify-content: flex-end; gap: 20px; }
.row-actions button {
  min-height: 44px;
  padding: 0 4px;
  background: none;
  border: 0;
  font-family: var(--hd-sans);
  font-weight: 600;
  font-size: var(--hd-t-small);
  cursor: pointer;
}
.cancel { color: var(--hd-pencil); }
.save { color: var(--hd-margin); }

/* Pencil, not the focus teal: teal here was the only teal on the screen and
   read as a link to somewhere else. It is a secondary thing to do with this
   book, so it wears the secondary colour. */
.booklet-link {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: 7px;
  padding: 0 4px 0 0;
  background: none;
  border: 0;
  color: var(--hd-pencil);
  font-family: var(--hd-sans);
  font-weight: 600;
  font-size: var(--hd-t-control);
  cursor: pointer;
}
</style>
